import {
  useCmmdRuntimeInventorySnapshot,
  type CmmdRuntimeInventoryResourceSnapshot,
} from "../../cmmd/cmmdRuntimeInventoryResource";
import { getStoredLocalDeviceRuntimeId } from "../../deviceRegistryIdentity";
import type {
  DesktopRuntimeExecutionSummary,
  DesktopRuntimeInventoryProjectSummary,
  ProjectSummary,
} from "./NewThreadLanding.logic";
import type { DesktopRuntimeInventoryProjectSelection } from "./NewThreadLanding.submission";

export interface DesktopRuntimeCatalog {
  readonly executionTargets: readonly DesktopRuntimeExecutionSummary[];
  readonly projects: readonly DesktopRuntimeInventoryProjectSummary[];
  readonly resolveProjectSelection: (
    summary: ProjectSummary | null,
  ) => DesktopRuntimeInventoryProjectSelection | null;
}

export function resolveDesktopRuntimeProjectSelection(input: {
  readonly activeProjectPresent: boolean;
  readonly summary: ProjectSummary | null;
  readonly catalog: DesktopRuntimeCatalog;
}): DesktopRuntimeInventoryProjectSelection | null {
  if (input.activeProjectPresent) return null;
  return input.catalog.resolveProjectSelection(input.summary);
}

/**
 * The projects a remote desktop is currently offering, given an inventory
 * snapshot. Pure, and separate from {@link useDesktopRuntimeCatalog}, so a caller
 * that only needs to know *how many* there are can read the snapshot it already
 * has without adding another subscription. The shared snapshot hook owns one
 * passive startup request, and the resource coalesces overlapping refreshes.
 *
 * The online/synced predicate lives here once. Re-deriving it at a counting site
 * would be a second definition of "a project you can actually work in".
 */
export function selectDesktopInventoryProjects(
  snapshot: CmmdRuntimeInventoryResourceSnapshot,
  currentRuntimeId: string | null,
): readonly DesktopRuntimeInventoryProjectSummary[] {
  return (snapshot.inventory?.runtimes ?? [])
    .filter(
      (entry) =>
        entry.runtime.runtimeKind === "desktop-local" && entry.runtime.id !== currentRuntimeId,
    )
    .flatMap((entry) =>
      entry.runtime.status.trim().toLowerCase() === "online" && entry.inventoryStatus === "synced"
        ? entry.projects.map((project) => ({
            id: project.projectId,
            environmentId: entry.runtime.id,
            name: project.title,
            runtimeLabel: entry.runtime.displayName,
          }))
        : [],
    );
}

export function useDesktopRuntimeCatalog(): DesktopRuntimeCatalog {
  const snapshot = useCmmdRuntimeInventorySnapshot();
  const currentRuntimeId = getStoredLocalDeviceRuntimeId();
  const runtimes = snapshot.inventory?.runtimes ?? [];

  const remoteDesktops = runtimes.filter(
    (entry) =>
      entry.runtime.runtimeKind === "desktop-local" && entry.runtime.id !== currentRuntimeId,
  );
  const executionTargets = remoteDesktops.map((entry) => ({
    environmentId: entry.runtime.id,
    label: entry.runtime.displayName,
    online: entry.runtime.status.trim().toLowerCase() === "online",
    inventorySynced: entry.inventoryStatus === "synced",
  }));
  const projects = selectDesktopInventoryProjects(snapshot, currentRuntimeId);

  const resolveProjectSelection = (
    summary: ProjectSummary | null,
  ): DesktopRuntimeInventoryProjectSelection | null => {
    if (summary === null) return null;
    const runtime = remoteDesktops.find(
      (entry) =>
        entry.runtime.id === summary.environmentId &&
        entry.inventoryStatus === "synced" &&
        entry.runtime.status.trim().toLowerCase() === "online",
    );
    const project = runtime?.projects.find((entry) => entry.projectId === summary.id) ?? null;
    return runtime && project
      ? {
          runtimeId: runtime.runtime.id,
          runtimeLabel: runtime.runtime.displayName,
          projectId: project.projectId,
          projectName: project.title,
        }
      : null;
  };

  return { executionTargets, projects, resolveProjectSelection };
}
